import { cn } from '@/lib/utils'
import { Mic, MicOff } from 'lucide-react'

interface DictationMicProps {
  isListening: boolean
  isSupported: boolean
  error: string | null
  disabled: boolean
  interimTranscript: string
  onToggle: () => void
}

/**
 * Presentational voice-input control shared by the interview and drill answer
 * boxes. Renders an overlay mic button (position it inside a `relative`
 * wrapper around the textarea), the in-progress interim transcript, and any
 * error message. Hook wiring (useSpeechToText + analytics) stays at each call
 * site because the analytics payloads differ.
 */
export function DictationMic({
  isListening,
  isSupported,
  error,
  disabled,
  interimTranscript,
  onToggle,
}: DictationMicProps) {
  if (!isSupported) return null

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-label={isListening ? 'Stop recording' : 'Start voice input'}
        title="Voice input sends audio to Google for transcription"
        className={cn(
          'absolute bottom-2.5 right-2.5 p-1 rounded-lg transition-colors disabled:opacity-40',
          isListening
            ? 'text-destructive hover:text-destructive/80'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        {isListening ? <MicOff className="h-4 w-4 animate-pulse" /> : <Mic className="h-4 w-4" />}
      </button>
      {isListening && interimTranscript && (
        <p className="px-1 pt-1 text-xs text-muted-foreground italic truncate">
          {interimTranscript}
        </p>
      )}
      {error && (
        <p role="alert" className="px-1 pt-1 text-xs text-destructive">
          {error}
        </p>
      )}
    </>
  )
}
